import React, { useEffect, useMemo, useState } from "react";
import { useAdminAuth } from "../AdminAuthContext";
import {
  PageHeader, Panel, StatCard, Select, Badge,
} from "../components/UIKit";

function BarChart({ data, keyField = "date", valueField = "avg_score", max = 100 }) {
  const w = 640;
  const h = 200;
  const padL = 30;
  const padB = 30;
  const padT = 16;
  const innerW = w - padL - 12;
  const innerH = h - padT - padB;
  const barW = data.length ? innerW / data.length - 4 : 20;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-auto">
      <defs>
        <linearGradient id="bg1" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#4de3c4" />
          <stop offset="100%" stopColor="#0a8670" />
        </linearGradient>
      </defs>
      {[0, 25, 50, 75, 100].map((t) => {
        const y = padT + innerH - (t / max) * innerH;
        return (
          <g key={t}>
            <line
              x1={padL}
              x2={w - 12}
              y1={y}
              y2={y}
              stroke="rgba(255,255,255,0.08)"
              strokeDasharray="2 4"
            />
            <text x={padL - 6} y={y + 3} fontSize="10" textAnchor="end" fill="rgba(255,255,255,0.6)">
              {t}
            </text>
          </g>
        );
      })}
      {data.map((d, i) => {
        const x = padL + i * (innerW / data.length) + 2;
        const v = d[valueField] || 0;
        const bh = (v / max) * innerH;
        const y = padT + innerH - bh;
        return (
          <g key={i}>
            <rect x={x} y={y} width={barW} height={Math.max(bh, 2)} rx={3} fill="url(#bg1)" />
            <text
              x={x + barW / 2}
              y={h - 10}
              fontSize="9"
              textAnchor="middle"
              fill="rgba(255,255,255,0.6)"
            >
              {d[keyField]?.slice(5)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function Donut({ data, colors }) {
  const total = Object.values(data).reduce((s, v) => s + v, 0) || 1;
  let offset = 0;
  const r = 60;
  const c = 2 * Math.PI * r;
  return (
    <div className="flex items-center gap-5">
      <svg width="150" height="150" viewBox="0 0 150 150">
        <g transform="translate(75,75) rotate(-90)">
          <circle r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="18" />
          {Object.entries(data).map(([k, v], i) => {
            const dash = (c * v) / total;
            const seg = (
              <circle
                key={k}
                r={r}
                fill="none"
                stroke={colors[k]}
                strokeWidth="18"
                strokeDasharray={`${dash} ${c}`}
                strokeDashoffset={-offset}
              />
            );
            offset += dash;
            return seg;
          })}
        </g>
      </svg>
      <div className="space-y-1.5">
        {Object.entries(data).map(([k, v]) => (
          <div key={k} className="flex items-center gap-2 text-[12.5px]">
            <span
              className="w-2.5 h-2.5 rounded-full"
              style={{ backgroundColor: colors[k] }}
            />
            <span className="text-white/85 w-16">{k.replace("_min", "")}</span>
            <span className="text-white font-semibold">{v}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminAnalytics() {
  const { api } = useAdminAuth();
  const [range, setRange] = useState("7");
  const [data, setData] = useState(null);

  useEffect(() => {
    api("/analytics/summary", { params: { range } }).then(setData).catch(() => {});
  }, [range, api]);

  if (!data) return <div className="p-8 text-white/60">Caricamento…</div>;

  return (
    <div className="p-8">
      <PageHeader
        title="Analytics Operativi"
        subtitle="KPI sul parco utenti e sugli esiti del monitoraggio"
        actions={
          <Select
            value={range}
            onChange={setRange}
            options={[
              { value: "7", label: "Ultimi 7 giorni" },
              { value: "30", label: "Ultimi 30 giorni" },
              { value: "90", label: "Ultimi 90 giorni" },
            ]}
          />
        }
      />

      <div className="grid grid-cols-4 gap-3 mb-4">
        <StatCard
          label="Sessioni totali"
          value={data.total_sessions}
          sub={`ultimi ${data.range_days} giorni`}
        />
        <StatCard
          label="Utenti attivi"
          value={data.active_users}
          sub="online nelle ultime 24h"
          accent="teal"
        />
        <StatCard
          label="Retention D1"
          value={`${Math.round(data.retention.post_notification_d1 * 100)}% `}
          sub="post-notifica"
          accent="sky"
        />
        <StatCard
          label="Retention D30"
          value={`${Math.round(data.retention.post_notification_d30 * 100)}% `}
          sub="post-notifica"
          accent="amber"
        />
      </div>

      <Panel title="Score medio giornaliero (integrato)">
        <BarChart data={data.daily_avg_score} />
      </Panel>

      <div className="mt-4 grid grid-cols-2 gap-4">
        <Panel title="Distribuzione fasi sonno">
          <Donut
            data={data.phases_pct}
            colors={{
              deep_min: "#0a8670",
              rem_min: "#a78bfa",
              light_min: "#67e8d1",
              awake_min: "#ffffff",
            }}
          />
        </Panel>

        <Panel title="Top 5 consigli generati">
          <div className="space-y-2">
            {data.top_tips.map((t, i) => (
              <div
                key={i}
                className="flex items-center justify-between rounded-lg bg-white/[0.04] border border-white/10 px-3 py-2"
              >
                <div className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-[#14c2a1]/20 border border-[#14c2a1]/30 text-[#67e8d1] text-[12px] flex items-center justify-center font-semibold">
                    {i + 1}
                  </span>
                  <span className="text-white text-[13px]">{t.title}</span>
                </div>
                <span className="text-white/70 text-[12px] font-mono">
                  {t.count}
                </span>
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
}
